import React, { useEffect, useState } from 'react'
import { Dimensions, StyleSheet } from 'react-native'

import { UploadButton } from 'partials/UploadButton'

import { Box } from 'widgets/Box'
import { Icon } from 'widgets/Icon'
import { Pressable } from 'widgets/Pressable'
import { Text } from 'widgets/Text'
import { VideoPlayer } from 'widgets/Video'

import commonStyles from 'utils/styles/commonStyles'
import { formatVideoDuration } from 'utils/time'

import { AllowedFileType, FileType, NativeFile } from 'types/file'

import { MediaPreview } from './widgets/MediaPreview'

interface MediaSelectorProps {
  allowedFileType: AllowedFileType
  audioSwitchDisabled: boolean
  clearFile: () => void
  isAudioEnabled: boolean
  selectedFileType?: FileType
  selectedFileUrl?: string
  setFile: ((file: NativeFile) => void) | ((file: React.ChangeEvent<HTMLInputElement>) => void)
  toggleIsAudioEnabled: () => void
  size?: 'oneThird' | 'half' | 'twoThird' | 'full'
}

const styles = StyleSheet.create({
  selectedFileContainer: {
    maxWidth: 300,
    maxHeight: 400,
    overflow: 'hidden',
  },
})

export const MediaSelector = ({
  allowedFileType,
  audioSwitchDisabled,
  clearFile,
  isAudioEnabled,
  selectedFileType,
  selectedFileUrl,
  setFile,
  toggleIsAudioEnabled,
  size = 'oneThird',
}: MediaSelectorProps) => {
  const [videoDuration, setVideoDuration] = useState(0)

  const handleVideoLoaded = (videoPlayer: VideoPlayer) => {
    setVideoDuration(videoPlayer?.getDuration())
  }

  useEffect(() => {
    if (!selectedFileUrl) {
      setVideoDuration(0)
    }
  }, [selectedFileUrl])

  const widthMultiplier = (): number => {
    switch (size) {
      case 'oneThird':
        return Dimensions.get('window').width * 0.3
      case 'half':
        return Dimensions.get('window').width * 0.45
      case 'twoThird':
        return Dimensions.get('window').width * 0.65
      case 'full':
        return Dimensions.get('window').width * 0.9
      default:
        return Dimensions.get('window').width
    }
  }

  return (
    <Box align='flex-start' justifyContent='space-between' width='100%'>
      <Box width='100%' align='center' justifyContent='center'>
        <Box
          align='center'
          justifyContent='center'
          borderRadius='sm'
          width={widthMultiplier()}
          height={widthMultiplier() * 1.2}
          border={!selectedFileUrl ? { width: 1, color: 'grey' } : undefined}
          backgroundColor={!selectedFileUrl ? 'grey' : 'black'}
          style={styles.selectedFileContainer}
        >
          {selectedFileUrl && (
            <Box
              zIndex={11}
              width='100%'
              height='100%'
              position='absolute'
              pointerEvents='box-none'
            >
              <Pressable onPress={() => clearFile()}>
                <Box padding='xs'>
                  <Icon name='x' color='white' size={30} />
                </Box>
              </Pressable>
            </Box>
          )}
          {!selectedFileUrl && (
            <Box zIndex={11} width='100%' height='100%' position='absolute'>
              <UploadButton fileType={allowedFileType} onFileSelect={setFile} />
            </Box>
          )}
          {selectedFileUrl && (
            <Box
              zIndex={11}
              width='100%'
              height='100%'
              position='absolute'
              pointerEvents='box-none'
              justifyContent='space-between'
              align='flex-end'
              flexDirection='row'
              padding='sm'
            >
              <Box // camera button
                zIndex={11}
                width={28}
                height={28}
                justifyContent='center'
                align='center'
                style={commonStyles.opacity90}
              >
                <Icon name='camera' color='white' size={22} />
                <Box position='absolute' width='100%' height='100%'>
                  <UploadButton fileType={allowedFileType} onFileSelect={setFile} />
                </Box>
              </Box>

              {selectedFileType === 'video' && (
                <Box height={28} align='center' justifyContent='center'>
                  <Text fontSize='sm' color='white'>
                    {formatVideoDuration(videoDuration)}
                  </Text>
                </Box>
              )}

              {selectedFileType === 'video' && (
                <Box // mute button
                  zIndex={11}
                  width={28}
                  height={28}
                  justifyContent='center'
                  align='center'
                  style={commonStyles.opacity90}
                >
                  <Pressable onPress={toggleIsAudioEnabled}>
                    <Icon name={isAudioEnabled ? 'volume-2' : 'volume-x'} color='white' size={22} />
                  </Pressable>
                </Box>
              )}
            </Box>
          )}
          <MediaPreview
            fileUrl={selectedFileUrl}
            isMuted={!isAudioEnabled}
            type={selectedFileType}
            handleVideoLoaded={handleVideoLoaded}
          />
        </Box>
      </Box>
    </Box>
  )
}
